/**
 * DataGridTable Component
 *
 * DataGrid wrapped in a RoundedCard with:
 * - Styled header row (brand tint background)
 * - Data rows with hover effects
 * - Pagination footer (brand tint background)
 *
 * Renders INSIDE the card.
 */

import {
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  TableColumnSizingOptions,
  SortDirection,
  Button,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { RoundedCard } from '../../common/RoundedCard';
import { useDataGridStyles } from '../../common/DataGrid/hooks/useDataGridStyles';

const useLocalStyles = makeStyles({
  card: {
    ...shorthands.padding('0'),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px'),
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px'),
    color: tokens.colorNeutralForeground3,
  },
  hideSelectionIndicator: {
    // Hide the radio/checkbox indicator but preserve the cell width for spacing
    // Fluent UI v9 uses custom indicator elements with fui- class prefixes
    '& [class*="fui-Radio__indicator"]': {
      visibility: 'hidden',
    },
    '& [class*="fui-Checkbox__indicator"]': {
      visibility: 'hidden',
    },
  },
});

export interface DataGridTableProps<T> {
  /** Items to display */
  items: T[];
  /** Column definitions */
  columns: TableColumnDefinition<T>[];
  /** Function to get unique row ID */
  getRowId: (item: T) => string | number;
  /** Column sizing options */
  columnSizingOptions?: TableColumnSizingOptions;
  /** Default sort state */
  defaultSortState?: {
    sortColumn: string;
    sortDirection: SortDirection;
  };
  /** Pagination info */
  pagination: {
    showing: number;
    total: number;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
  };
  /** Enable selection mode */
  selectable?: boolean;
  /** Currently selected IDs */
  selectedIds?: Set<string | number>;
  /** Callback when selection changes */
  onSelectionChange?: (ids: Set<string | number>) => void;
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
}

export function DataGridTable<T>({
  items,
  columns,
  getRowId,
  columnSizingOptions,
  defaultSortState,
  pagination,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  loading = false,
  emptyMessage = 'No data available',
}: DataGridTableProps<T>) {
  const gridStyles = useDataGridStyles();
  const localStyles = useLocalStyles();

  const handleSelectionChange = (
    _event: React.SyntheticEvent,
    data: { selectedItems: Set<string | number> }
  ) => {
    if (onSelectionChange) {
      // Convert string IDs back to their original type
      const newSelected = new Set<string | number>();
      data.selectedItems.forEach((rowId) => {
        const item = items.find((i) => String(getRowId(i)) === rowId);
        if (item) {
          newSelected.add(getRowId(item));
        }
      });
      onSelectionChange(newSelected);
    }
  };

  const hasPages = pagination.page !== undefined && pagination.totalPages !== undefined;

  return (
    <RoundedCard className={localStyles.card} style={{ padding: 0, gap: 0 }}>
      <div className={gridStyles.tableContainer}>
        <DataGrid
          items={items}
          columns={columns}
          sortable
          resizableColumns
          columnSizingOptions={columnSizingOptions}
          defaultSortState={defaultSortState}
          selectionMode={selectable ? 'multiselect' : 'single'}
          selectedItems={new Set(Array.from(selectedIds).map(String))}
          onSelectionChange={handleSelectionChange}
          getRowId={(item) => String(getRowId(item))}
          className={mergeClasses(gridStyles.table, !selectable && localStyles.hideSelectionIndicator)}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<T>>
            {({ item, rowId }) => (
              <DataGridRow<T> key={rowId}>
                {({ renderCell }) => (
                  <DataGridCell>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
        {loading && (
          <div className={localStyles.loadingContainer}>
            <Spinner label="Loading..." />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className={localStyles.emptyContainer}>
            <Text>{emptyMessage}</Text>
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      <div className={gridStyles.pagination}>
        <span className={gridStyles.paginationInfo}>
          Showing {pagination.showing} of {pagination.total}
        </span>
        {hasPages && (
          <div className={gridStyles.paginationControls}>
            <Button
              appearance="subtle"
              disabled={pagination.page === 1 || !pagination.onPageChange}
              onClick={() => pagination.onPageChange?.(pagination.page! - 1)}
            >
              Previous
            </Button>
            <span className={gridStyles.pageText}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              appearance="subtle"
              disabled={pagination.page === pagination.totalPages || !pagination.onPageChange}
              onClick={() => pagination.onPageChange?.(pagination.page! + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </RoundedCard>
  );
}
